import type { JobLocationUpdate, JobOrderUpdate, RowKey, Shift } from "@domain/CRM/shift";
import {
	buildShiftJobUpdates,
	computeJobDropoffLinks,
	computeMixedOrderUpdates,
	computeStopMembershipPlan,
	finalRowKeysForShift,
	jobRowKey,
	newStopRowKey,
	parseRowKey,
} from "@domain/CRM/shift";
import type { MapFeatureEdit, PullInZone } from "@domain/CRM/job";
import { buildMapFeatureEdits } from "@domain/CRM/job";
import type { IShifts } from "@ports/shifts/IShifts";
import type { IShiftDropoffs } from "@ports/shifts/IShiftDropoffs";
import type { IJobs } from "@ports/jobs/IJobs";
import type { Job } from "@domain/CRM/job";
import {
	dropBoardCaches,
	firstIncomingLookFrom,
	readIncomingJobs,
	reloadAfterSave,
	storeRereadJobs,
	type AfterSaveReread,
	type RereadJobChanges,
	type SchedulingBoardDeps,
} from "./shiftsUseCases.board";

/** The ports the scheduling save writes through. */
export interface ScheduleSaveDeps {
	shiftsApi: IShifts;
	/** Junction persistence for dropoff stops (create/re-link/delete on save). */
	stopsApi: IShiftDropoffs;
	/** `setOrders` persists the run-sequence projection (`Jobs.Order`); `setDropoffs` the
	 *  sample-routing link (`Jobs."Dropoffs/Shipping"`); `setLocations` the cluster pull-in
	 *  point (`Jobs.Location`); `applyMapFeatureEdits` the pull-in zone deltas
	 *  (`Jobs."Map Features GeoJSON (code)"`). */
	jobsApi: Pick<IJobs, "setOrders" | "setDropoffs" | "setLocations" | "applyMapFeatureEdits">;
}

export interface ShiftsUseCasesDeps extends ScheduleSaveDeps, SchedulingBoardDeps {}

/** One batch of staged scheduling edits to persist. */
export interface SaveScheduleChangesInput {
	/** Brand-new shifts to create (already mapped from the UI's pending drafts to Shift fields). */
	creates: Partial<Shift>[];
	/** The board's loaded shifts; used to resolve which ones the staged maps touch. The touched
	 *  ones are re-read live before merging — see `readTouchedShiftsLive`. */
	existingShifts: Shift[];
	/** shiftId → jobIds to add. */
	additions: ReadonlyMap<string, ReadonlySet<string>>;
	/** shiftId → jobIds to remove. */
	removals: ReadonlyMap<string, ReadonlySet<string>>;
	/** shiftId → intended ordered jobIds for touched existing shifts (selection-sequence/anchor
	 *  inserts and row reorders). Reconciled against merged membership; a shift present only here
	 *  is a pure reorder. */
	orderedJobIdsByShiftId: ReadonlyMap<string, readonly string[]>;
	/** Current `Jobs.Order` for every loaded shift/backlog job; drives the minimal-write diff. */
	knownOrderByJobId: ReadonlyMap<string, number | undefined>;

	// ── Dropoff stops (all optional — a stop-free save is exactly the old behavior) ──────────
	/** shiftId → committed junction ids, from the loaded stops. */
	committedStopsByShiftId?: ReadonlyMap<string, readonly string[]>;
	/** shiftId → staged MIXED sequence (row keys `job:`/`stop:`/`newstop:`). May still contain
	 *  removal-staged rows (struck-through in the UI); they're filtered here. Where present it
	 *  is the ordering authority for that shift. */
	orderedRowKeysByShiftId?: ReadonlyMap<string, readonly RowKey[]>;
	/** Staged new stops' metadata by temp key. */
	newStopsByKey?: ReadonlyMap<string, { dropoffId: string }>;
	/** Committed stops staged for deletion (the portal deletes; MC orphans). */
	removedStopIds?: ReadonlySet<string>;
	/** Per pending-create index: trailing new-stop temp keys (drafts append stops after jobs). */
	createStopKeysByIndex?: ReadonlyMap<number, readonly string[]>;
	/** Current `ShiftDropoffs.Order` per committed stop; minimal-write diff. */
	knownStopOrderByStopId?: ReadonlyMap<string, number | undefined>;
	/** Current `Jobs."Dropoffs/Shipping"` head per job; minimal-write diff for the tagging. */
	knownDropoffLinkByJobId?: ReadonlyMap<string, string | undefined>;

	// ── Cluster pull-in points (both optional — a location-free save is exactly the old behavior) ──
	/** jobId → the `"lat,lng"` point staged for it. Every job in one cluster shares the string. */
	stagedLocationByJobId?: ReadonlyMap<string, string>;
	/** Current `Jobs.Location` per job; drives the minimal-write diff, like `knownOrderByJobId`. */
	knownLocationByJobId?: ReadonlyMap<string, string | undefined>;

	// ── Pull-in zones (both optional — a zone-free save is exactly the old behavior) ─────────
	/** jobId → the zones drawn for it this session. Sent as DELTAS: the endpoint merges them
	 *  into the job's current column, so there is no baseline to diff against here. */
	stagedPullInZonesByJobId?: ReadonlyMap<string, readonly PullInZone[]>;
	/** Jobs whose committed pull-in zones are cleared on this save (before any staged zones of
	 *  theirs are appended — clear-then-draw is a replace). */
	clearedPullInZoneJobIds?: ReadonlySet<string>;
}

export interface SaveScheduleChangesResult {
	/** The `Jobs.Order` writes actually persisted. The caller must fold these into whatever it
	 *  feeds back as `knownOrderByJobId` — job refetches keyed on the id *set* won't observe a
	 *  pure reorder, so the baseline would otherwise go stale and mis-diff the next save. */
	orderUpdates: JobOrderUpdate[];
	/** The `Jobs.Location` writes actually persisted. Fold these into whatever the caller feeds
	 *  back as `knownLocationByJobId` — for the same reason as `orderUpdates`: a job refetch keyed
	 *  on the id *set* won't observe a location-only change, so the baseline would go stale and
	 *  mis-diff the next save. */
	locationUpdates: JobLocationUpdate[];
	/** The pull-in zone edits sent. Informational: nothing to fold back — the post-save reconcile
	 *  re-reads the jobs, which is when the merged column shows up. */
	mapFeatureEdits: MapFeatureEdit[];
	/** Every job this save wrote, or moved onto or off a shift — what the board re-reads after it,
	 *  instead of both whole job lists. */
	touchedJobIds: string[];
	/** Whether the save created, re-linked, re-ordered or deleted any dropoff stop. */
	stopsChanged: boolean;
	/** The shift each create became, by its index in `creates` (undefined if none came back). */
	createdShiftIds: (string | undefined)[];
	/** The stop each `newstop:` temp key became — edits staged while the save ran name them. */
	createdStopIdByTempKey: ReadonlyMap<string, string>;
}

/**
 * Write path for the scheduling board. Encapsulates the "save staged shift edits" orchestration
 * so VMs depend on this use case rather than `API.Data.Shifts` + the merge rule inline. The
 * `(current ∪ additions) \ removals` math lives in the `@domain/CRM/shift` `buildShiftJobUpdates`
 * operation; the mixed-sequence stop planning lives in `stopPlanning`; this use case sequences
 * the create/update/delete calls through the ports.
 */
export interface ShiftsUseCases {
	/** Create the pending shifts and update every existing shift whose job set changed. Each call
	 *  no-ops the half (create / update) it has nothing for. */
	saveScheduleChanges(input: SaveScheduleChangesInput): Promise<SaveScheduleChangesResult>;
	/** After a save, re-read what it touched (see `reloadAfterSave`). */
	reloadAfterSave(touched: { touchedJobIds: readonly string[]; stopsChanged: boolean }): Promise<AfterSaveReread>;
	/** Drop the board's cached shifts, stops and backlog, so the next reads refetch. */
	dropBoardCaches(): void;
	/** Swap re-read jobs into the cached job lists; returns the same changes for the board's own. */
	storeRereadJobs(
		reread: readonly Job[],
		rereadIds: readonly string[],
		opts: { showTestJobs: boolean },
	): RereadJobChanges;
	/** Where the board's first look for incoming jobs starts (see `firstIncomingLookFrom`). */
	firstIncomingLookFrom(mount: { now: number; listsFromCache: boolean }): number;
	/** One look for incoming jobs (see `readIncomingJobs`). */
	readIncomingJobs(window: { from: number; startedAt: number }): Promise<{ jobs: Job[]; nextFrom: number }>;
}

const EMPTY_ROW_KEYS: ReadonlyMap<string, readonly RowKey[]> = new Map();
const EMPTY_STOPS: ReadonlyMap<string, readonly string[]> = new Map();
const EMPTY_META: ReadonlyMap<string, { dropoffId: string }> = new Map();
const EMPTY_IDS: ReadonlySet<string> = new Set();
const EMPTY_ORDERS: ReadonlyMap<string, number | undefined> = new Map();
const EMPTY_LOCATIONS: ReadonlyMap<string, string> = new Map();
const EMPTY_KNOWN_LOCATIONS: ReadonlyMap<string, string | undefined> = new Map();
const EMPTY_ZONES: ReadonlyMap<string, readonly PullInZone[]> = new Map();

export async function saveScheduleChanges(
	deps: ScheduleSaveDeps,
	input: SaveScheduleChangesInput,
): Promise<SaveScheduleChangesResult> {
	const stagedRowKeys = input.orderedRowKeysByShiftId ?? EMPTY_ROW_KEYS;
	const committedStops = input.committedStopsByShiftId ?? EMPTY_STOPS;
	const newStopsByKey = input.newStopsByKey ?? EMPTY_META;
	const removedStopIds = input.removedStopIds ?? EMPTY_IDS;
	const createStopKeys = input.createStopKeysByIndex ?? new Map<number, readonly string[]>();

	// Merge into what Airtable holds NOW, not the board's copy: that copy can be many minutes old,
	// and writing a membership computed from it reverts whatever another dispatcher, Mission
	// Control or the Airtable UI changed on the shift meanwhile.
	const existingShifts = await readTouchedShiftsLive(deps, input, stagedRowKeys);

	const updates = buildShiftJobUpdates(existingShifts, input.additions, input.removals, input.orderedJobIdsByShiftId);

	// ── Final mixed sequence per shift ─────────────────────────────────────────────────────────
	// Every shift with a staged sequence or a membership change (see `finalRowKeysForShift`).
	const finalJobIdsByShiftId = new Map<string, readonly string[]>();
	for (const update of updates) finalJobIdsByShiftId.set(update.id, update.jobIds);
	const knownStopOrders = input.knownStopOrderByStopId ?? EMPTY_ORDERS;
	const finalRowKeysByShiftId = new Map<string, RowKey[]>();
	const resequencedShiftIds = new Set<string>([...stagedRowKeys.keys(), ...finalJobIdsByShiftId.keys()]);
	for (const shiftId of resequencedShiftIds) {
		const membership = finalJobIdsByShiftId.get(shiftId) ?? existingShifts.find((s) => s.id === shiftId)?.jobIds ?? [];
		const finalRowKeys = finalRowKeysForShift({
			membership,
			staged: stagedRowKeys.get(shiftId),
			committedStopIds: committedStops.get(shiftId) ?? [],
			removedStopIds,
			knownOrderByJobId: input.knownOrderByJobId,
			knownStopOrderByStopId: knownStopOrders,
		});
		finalRowKeysByShiftId.set(shiftId, finalRowKeys);
	}

	// Pending drafts: jobs in draft order, then the draft's trailing stops.
	const createRowKeyLists: RowKey[][] = input.creates.map((create, index) => [
		...(create.jobIds ?? []).map(jobRowKey),
		...(createStopKeys.get(index) ?? []).map(newStopRowKey),
	]);

	// ── Order + membership plans (pure) ────────────────────────────────────────────────────────
	const netRemoved = jobsLeavingShifts(input.removals, existingShifts);
	const { jobOrders, stopOrders, newStopOrders } = computeMixedOrderUpdates(
		[...createRowKeyLists, ...finalRowKeysByShiftId.values()],
		netRemoved,
		input.knownOrderByJobId,
		knownStopOrders,
	);
	const membershipPlan = computeStopMembershipPlan(
		committedStops,
		finalRowKeysByShiftId,
		newStopsByKey,
		removedStopIds,
	);

	// ── Execute: shifts first (creates resolve the draft ids the stop creates need) ───────────
	const [createdShifts] = await Promise.all([
		input.creates.length > 0 ? deps.shiftsApi.createMany(input.creates) : Promise.resolve([] as Shift[]),
		// Jobs link only: the other fields on the board's copy may be stale, and re-sending them
		// would put back an operator, robot, rig or date someone changed elsewhere.
		updates.length > 0
			? deps.shiftsApi.setJobs(updates.map(({ id, jobIds }) => ({ id, jobIds })))
			: Promise.resolve([]),
	]);

	// Junction membership: creates carry their mixed order + the jobs accumulated before them
	// (MC sets `jobs` at create; Airtable mirrors it onto the jobs' Dropoffs/Shipping).
	const jobsBeforeKey = (list: RowKey[], targetKey: RowKey): string[] => {
		const acc: string[] = [];
		for (const key of list) {
			if (key === targetKey) return acc;
			const parsed = parseRowKey(key);
			if (parsed.kind === "job") acc.push(parsed.id);
			else acc.length = 0;
		}
		return acc;
	};
	const draftStopCreates = input.creates.flatMap((_, index) => {
		const list = createRowKeyLists[index];
		const shiftId = createdShifts[index]?.id;
		if (!shiftId) return [];
		return (createStopKeys.get(index) ?? []).flatMap((tempKey) => {
			const meta = newStopsByKey.get(tempKey);
			if (!meta) return [];
			return [
				{
					tempKey,
					stop: {
						dropoffId: meta.dropoffId,
						shiftId,
						order: newStopOrders.get(tempKey) ?? list.length,
						jobIds: jobsBeforeKey(list, newStopRowKey(tempKey)),
					},
				},
			];
		});
	});
	const confirmedStopCreates = membershipPlan.creates.map((create) => ({
		tempKey: create.tempKey,
		stop: {
			dropoffId: create.dropoffId,
			shiftId: create.shiftId,
			order: newStopOrders.get(create.tempKey) ?? 0,
			jobIds: jobsBeforeKey(finalRowKeysByShiftId.get(create.shiftId) ?? [], newStopRowKey(create.tempKey)),
		},
	}));
	const stopCreates = [...confirmedStopCreates, ...draftStopCreates];

	// Merge re-links and re-orders into one update per stop.
	const stopUpdateById = new Map<string, { id: string; shiftId?: string; order?: number }>();
	for (const relink of membershipPlan.relinks)
		stopUpdateById.set(relink.stopId, { id: relink.stopId, shiftId: relink.shiftId });
	for (const orderUpdate of stopOrders) {
		const existing = stopUpdateById.get(orderUpdate.stopId) ?? { id: orderUpdate.stopId };
		stopUpdateById.set(orderUpdate.stopId, { ...existing, order: orderUpdate.order });
	}

	const [createdStops] = await Promise.all([
		stopCreates.length > 0 ? deps.stopsApi.createMany(stopCreates.map((c) => c.stop)) : Promise.resolve([]),
		stopUpdateById.size > 0 ? deps.stopsApi.updateMany([...stopUpdateById.values()]) : Promise.resolve([]),
		membershipPlan.deletes.length > 0 ? deps.stopsApi.deleteMany(membershipPlan.deletes) : Promise.resolve(),
	]);

	// Order is a projection of each shift's mixed sequence; write after the links are in place.
	if (jobOrders.length > 0) await deps.jobsApi.setOrders(jobOrders);

	// ── Sample routing (MC's accumulator) over the RESOLVED sequences ──────────────────────────
	const createdIdByTempKey = new Map<string, string>();
	stopCreates.forEach((create, index) => {
		const created = createdStops[index];
		if (created?.id) createdIdByTempKey.set(create.tempKey, created.id);
	});
	const resolvedSequences = [...createRowKeyLists, ...finalRowKeysByShiftId.values()].map((list) => {
		const rows: { kind: "job" | "stop"; id: string }[] = [];
		for (const key of list) {
			const parsed = parseRowKey(key);
			if (parsed.kind === "job") rows.push({ kind: "job", id: parsed.id });
			else if (parsed.kind === "stop") rows.push({ kind: "stop", id: parsed.id });
			else {
				const created = createdIdByTempKey.get(parsed.id);
				if (created) rows.push({ kind: "stop", id: created });
			}
		}
		return rows;
	});
	const dropoffLinks = computeJobDropoffLinks(resolvedSequences, input.knownDropoffLinkByJobId ?? new Map());
	if (dropoffLinks.length > 0) await deps.jobsApi.setDropoffs(dropoffLinks);

	// ── Cluster pull-in points ──────────────────────────────────────────────────────────────────
	// Independent of the shift math: a point belongs to the JOB, not to the shift, so a job
	// unassigned after its point was staged still keeps the point. Minimal-write diff against the
	// known baseline, exactly like Order.
	const stagedLocations = input.stagedLocationByJobId ?? EMPTY_LOCATIONS;
	const knownLocations = input.knownLocationByJobId ?? EMPTY_KNOWN_LOCATIONS;
	const jobLocations: JobLocationUpdate[] = [];
	for (const [jobId, location] of stagedLocations) {
		if (knownLocations.get(jobId) === location) continue;
		jobLocations.push({ jobId, location });
	}
	if (jobLocations.length > 0) await deps.jobsApi.setLocations(jobLocations);

	// ── Pull-in zones (map features) ────────────────────────────────────────────────────────────
	// Deltas, not results: the endpoint reads each job's current column and merges, so a stale
	// board can never drop zones another dispatcher added. Like the point, a zone belongs to the
	// JOB, so it is independent of the shift math above.
	const mapFeatureEdits = buildMapFeatureEdits(
		input.stagedPullInZonesByJobId ?? EMPTY_ZONES,
		input.clearedPullInZoneJobIds ?? EMPTY_IDS,
	);
	if (mapFeatureEdits.length > 0) await deps.jobsApi.applyMapFeatureEdits(mapFeatureEdits);

	const stopsChanged = stopCreates.length > 0 || stopUpdateById.size > 0 || membershipPlan.deletes.length > 0;
	return {
		orderUpdates: jobOrders,
		locationUpdates: jobLocations,
		mapFeatureEdits,
		touchedJobIds: jobsTouchedBySave({ input, jobOrders, dropoffLinks, jobLocations, mapFeatureEdits }),
		stopsChanged,
		createdShiftIds: input.creates.map((_, index) => createdShifts[index]?.id),
		createdStopIdByTempKey: createdIdByTempKey,
	};
}

/** A write the save made to one job. */
interface JobWrite {
	readonly jobId: string;
}

/** Every job a save wrote, or moved onto or off a shift: what the board re-reads after it. */
function jobsTouchedBySave(save: {
	input: SaveScheduleChangesInput;
	jobOrders: readonly JobWrite[];
	dropoffLinks: readonly JobWrite[];
	jobLocations: readonly JobWrite[];
	mapFeatureEdits: readonly JobWrite[];
}): string[] {
	const { input } = save;
	const touched = new Set<string>([
		...[...input.additions.values(), ...input.removals.values()].flatMap((ids) => [...ids]),
		...input.creates.flatMap((create) => create.jobIds ?? []),
		...[save.jobOrders, save.dropoffLinks, save.jobLocations, save.mapFeatureEdits].flatMap((writes) =>
			writes.map((write) => write.jobId),
		),
	]);
	return [...touched];
}

/** Only the jobs this save actually takes off a shift, judged against the live shifts: a staged
 *  removal of a job another writer has already moved elsewhere must not clear the Order that
 *  writer gave it. */
function jobsLeavingShifts(
	removals: ReadonlyMap<string, ReadonlySet<string>>,
	currentShifts: readonly Shift[],
): Set<string> {
	const leaving = new Set<string>();
	for (const [shiftId, jobIds] of removals) {
		const current = currentShifts.find((s) => s.id === shiftId)?.jobIds ?? [];
		for (const id of jobIds) if (current.includes(id)) leaving.add(id);
	}
	return leaving;
}

/** A save that edited shifts which no longer exist in Airtable. Thrown before anything is written,
 *  so the caller can discard those shifts' edits and let the user save the rest. */
export class MissingShiftsError extends Error {
	constructor(
		readonly shiftIds: readonly string[],
		names: readonly string[],
	) {
		super(`${names.join(", ")} no longer ${shiftIds.length === 1 ? "exists" : "exist"} in Airtable.`);
		this.name = "MissingShiftsError";
	}
}

/**
 * The touched shifts (any staged addition, removal, reorder or mixed sequence) re-read live, with
 * the untouched ones left as loaded. A touched shift that no longer exists fails the save before
 * anything is written: dropping its edits silently would leave the user believing those jobs were
 * scheduled, and saving the rest would still apply the removals paired with them.
 */
async function readTouchedShiftsLive(
	deps: ScheduleSaveDeps,
	input: SaveScheduleChangesInput,
	stagedRowKeys: ReadonlyMap<string, readonly RowKey[]>,
): Promise<Shift[]> {
	// Every touched id, including one the board no longer lists: after a failed save refreshed it,
	// a deleted shift drops out of `existingShifts` while its edits are still staged.
	const touched = new Set<string>([
		...input.additions.keys(),
		...input.removals.keys(),
		...input.orderedJobIdsByShiftId.keys(),
		...stagedRowKeys.keys(),
	]);
	if (touched.size === 0) return input.existingShifts;
	const live = await deps.shiftsApi.getLive([...touched]);
	const liveById = new Map(live.map((shift) => [shift.id, shift]));
	const missing = [...touched].filter((id) => !liveById.has(id));
	if (missing.length > 0) {
		const names = missing.map((id) => input.existingShifts.find((s) => s.id === id)?.name || id);
		throw new MissingShiftsError(missing, names);
	}
	const loaded = new Set(input.existingShifts.map((s) => s.id));
	return [
		...input.existingShifts.map((shift) => liveById.get(shift.id) ?? shift),
		...live.filter((shift) => !loaded.has(shift.id)),
	];
}

export function makeShiftsUseCases(deps: ShiftsUseCasesDeps): ShiftsUseCases {
	return {
		saveScheduleChanges: (input) => saveScheduleChanges(deps, input),
		reloadAfterSave: (touched) => reloadAfterSave(deps, touched),
		dropBoardCaches: () => dropBoardCaches(deps),
		storeRereadJobs: (reread, rereadIds, opts) => storeRereadJobs(deps, reread, rereadIds, opts),
		firstIncomingLookFrom: (mount) => firstIncomingLookFrom(deps, mount),
		readIncomingJobs: (window) => readIncomingJobs(deps, window),
	};
}
